import axios from 'axios';
import { useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { onboarding, reenviarVerificacion } from '../services/api';
import { NavLogo } from '../components/LogoLink';
import { formatearInput, formatearTelefono } from '../utils/phone';
import PasswordInput from '../components/PasswordInput';

const BACKEND = process.env.REACT_APP_API_URL || 'http://localhost:8000';

function calcularFortaleza(pwd) {
  const checks = [
    pwd.length >= 8,
    /[A-Z]/.test(pwd),
    /[!@#$%^&*()\-_=+{}|;:",.<>?/\\]/.test(pwd),
    /[0-9]/.test(pwd),
  ];
  const nivel = checks.filter(Boolean).length;
  const labels = ['', 'Muy débil', 'Débil', 'Intermedia', 'Fuerte'];
  const colors = ['', '#E63946', '#f97316', '#C9A84C', '#4ade80'];
  return { nivel, label: labels[nivel], color: colors[nivel], pct: nivel * 25, checks };
}

const CHECK_LABELS = ['8+ caracteres', 'Mayúscula', 'Caracter especial', 'Número'];

const PASOS = ['Tu cuenta', 'Tu barberia', 'Pago'];

const PLANES_INFO = {
  pro:     { label: 'Pro',     color: '#C9A84C', mensual: 29,  anual: 232,  desc_mensual: 'Hasta 3 barberos', desc_anual: 'Hasta 3 barberos · $19/mes' },
  premium: { label: 'Premium', color: '#a78bfa', mensual: 59,  anual: 472,  desc_mensual: 'Barberos ilimitados', desc_anual: 'Barberos ilimitados · $39/mes' },
};

export default function Onboarding() {
  const location = useLocation();
  const navigate = useNavigate();
  const planInicial = location.state?.plan || 'pro';
  const anualInicial = location.state?.anual || false;

  const [paso, setPaso] = useState(0);
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState('');
  const [anual, setAnual] = useState(anualInicial);
  const [stripeError, setStripeError] = useState(false);
  const [tempToken, setTempToken] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [tcAceptado, setTcAceptado] = useState(false);
  const [verificacionPendiente, setVerificacionPendiente] = useState(false);
  const [form, setForm] = useState({
    email: '', password: '',
    nombre_barberia: '', direccion: '', telefono: '',
    plan: planInicial,
  });

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
    setError('');
  };

  const fortaleza = calcularFortaleza(form.password);

  const handleRegistro = () => {
    if (!form.email || !form.password) { setError('Email y contraseña son obligatorios'); return; }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) { setError('El email ingresado no es válido'); return; }
    if (fortaleza.nivel < 3) { setError('La contraseña es muy débil. Usá mayúsculas, números y caracteres especiales.'); return; }
    if (confirmPassword && form.password !== confirmPassword) { setError('Las contraseñas no coinciden'); return; }
    if (!confirmPassword) { setError('Confirmá tu contraseña'); return; }
    if (!tcAceptado) { setError('Debes aceptar los Términos y Condiciones para continuar'); return; }
    setPaso(1);
  };

  const handleBarberia = async () => {
    if (!form.nombre_barberia.trim()) { setError('El nombre de la barberia es obligatorio'); return; }
    setCargando(true); setError('');
    try {
      const res = await onboarding({
        ...form,
        telefono: form.telefono ? formatearTelefono(form.telefono) : '',
      });
      setTempToken(res.data.access_token);
      setPaso(2);
    } catch (err) {
      const status = err.response?.status;
      const detail = err.response?.data?.detail;
      if (!err.response) {
        setError('No se pudo conectar con el servidor.');
      } else if (status === 422) {
        const msg = Array.isArray(detail) ? detail.map(e => e.msg || JSON.stringify(e)).join(', ') : 'Datos invalidos.';
        setError(msg);
      } else if (typeof detail === 'string') {
        if (detail === 'Datos de registro invalidos' || detail.toLowerCase().includes('ya esta registrado') || detail.toLowerCase().includes('already')) {
          setPaso(0);
          setError('Este email ya está registrado. Iniciá sesión o usá otro email.');
        } else {
          setError(detail);
        }
      } else {
        setError(`Error del servidor (${status || 'desconocido'}). Intenta de nuevo.`);
      }
    } finally {
      setCargando(false);
    }
  };

  const handleIrAStripe = async () => {
    setCargando(true); setStripeError(false);
    try {
      const checkoutRes = await axios.post(
        `${BACKEND}/suscripcion/checkout`,
        { plan: form.plan, periodo: anual ? 'anual' : 'mensual' },
        { headers: { Authorization: `Bearer ${tempToken}` }, withCredentials: true, timeout: 70000 }
      );
      window.location.href = checkoutRes.data.checkout_url;
    } catch {
      setStripeError(true);
      setCargando(false);
    }
  };

  return (
    <div style={{
      minHeight: '100vh', background: '#0A0A0A',
      display: 'flex', flexDirection: 'column', alignItems: 'center',
      justifyContent: 'center', padding: '24px',
      fontFamily: "'DM Sans', sans-serif",
    }}>
      {/* Glow de fondo */}
      <div style={{
        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, pointerEvents: 'none',
        background: 'radial-gradient(ellipse 70% 45% at 50% 0%, rgba(201,168,76,0.1) 0%, transparent 70%)',
      }} />

      {/* Back */}
      <div style={{ position: 'absolute', top: 20, left: 24, zIndex: 1 }}>
        <NavLogo to="/" />
      </div>

      <div style={{ width: '100%', maxWidth: 440, position: 'relative' }}>
        {/* Badge de marca */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, marginBottom: 20 }}>
          <div style={{
            width: 40, height: 40, borderRadius: '50%',
            background: 'rgba(201,168,76,0.12)', border: '1px solid rgba(201,168,76,0.3)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#C9A84C" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
              <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z"/>
              <polyline points="9 22 9 12 15 12 15 22"/>
            </svg>
          </div>
          <div>
            <p style={{ fontFamily: "'Bebas Neue'", fontSize: 20, letterSpacing: '0.1em', color: '#C9A84C', margin: 0, lineHeight: 1 }}>
              Registrá tu barbería
            </p>
            <p style={{ fontSize: 11, color: '#555', margin: 0, letterSpacing: '0.04em' }}>BarberSaaS · {(!anual && form.plan === 'pro') ? '14 días gratis' : 'acceso inmediato'}</p>
          </div>
        </div>

        {/* Stepper */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0, marginBottom: 32 }}>
          {PASOS.map((nombre, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center' }}>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
                <div style={{
                  width: 32, height: 32, borderRadius: '50%',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 13, fontWeight: 700,
                  background: i < paso ? 'rgba(201,168,76,0.2)' : i === paso ? '#C9A84C' : 'var(--bg-card)',
                  color: i < paso ? '#C9A84C' : i === paso ? '#0A0A0A' : 'var(--text-muted)',
                  border: i < paso ? '1px solid #C9A84C' : i === paso ? 'none' : '1px solid var(--border)',
                  transition: 'all 0.3s',
                }}>
                  {i < paso ? (
                    <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                      <path d="M2 7l3.5 3.5L12 4" stroke="#C9A84C" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  ) : i + 1}
                </div>
                <span style={{ fontSize: 11, color: i === paso ? '#C9A84C' : 'var(--text-muted)', display: 'none' }}
                  className="sm:block">{nombre}</span>
              </div>
              {i < PASOS.length - 1 && (
                <div style={{
                  width: 56, height: 1, margin: '0 8px', marginBottom: 18,
                  background: i < paso ? '#C9A84C' : 'var(--border)',
                  transition: 'background 0.3s',
                }}/>
              )}
            </div>
          ))}
        </div>

        {/* Card */}
        <div style={{
          background: '#111',
          border: '1px solid rgba(201,168,76,0.15)',
          borderRadius: 18,
          overflow: 'hidden',
          boxShadow: '0 24px 64px rgba(0,0,0,0.6), 0 0 0 1px rgba(201,168,76,0.05)',
        }}>
          {/* Franja dorada superior */}
          <div style={{ height: 3, background: 'linear-gradient(90deg, transparent, #C9A84C 40%, #e8c96a 60%, transparent)' }} />
          <div style={{ padding: '28px 28px 24px' }}>
          {error && (
            <div style={{
              background: 'rgba(230,57,70,0.08)', border: '1px solid rgba(230,57,70,0.25)',
              borderRadius: 10, padding: '10px 14px', fontSize: 13, color: '#E63946',
              marginBottom: 16,
            }}>
              {error}
            </div>
          )}

          {/* Paso 0: Cuenta */}
          {paso === 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <h2 style={{ fontFamily: "'Bebas Neue'", fontSize: 24, letterSpacing: '0.06em', margin: 0 }}>
                Crea tu cuenta
              </h2>

              {/* Plan selector */}
              <div>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                  <label style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                    Elegí tu plan — <span style={{ color: '#C9A84C' }}>{anual ? 'ahorrás 33%' : form.plan === 'pro' ? '14 días gratis' : 'acceso inmediato'}</span>
                  </label>
                  {/* Toggle mensual / anual */}
                  <button
                    onClick={() => setAnual(a => !a)}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 0,
                      background: 'var(--bg-secondary)', border: '1px solid var(--border)',
                      borderRadius: 100, padding: 3,
                      cursor: 'pointer', fontFamily: "'DM Sans'",
                    }}
                  >
                    <span style={{
                      padding: '4px 12px', borderRadius: 100, fontSize: 11, fontWeight: 700,
                      background: !anual ? '#C9A84C' : 'transparent',
                      color: !anual ? '#0A0A0A' : 'var(--text-muted)',
                      transition: 'all 0.2s',
                    }}>
                      Mensual
                    </span>
                    <span style={{
                      padding: '4px 12px', borderRadius: 100, fontSize: 11, fontWeight: 700,
                      background: anual ? '#4ade80' : 'transparent',
                      color: anual ? '#0A0A0A' : 'var(--text-muted)',
                      transition: 'all 0.2s',
                    }}>
                      Anual
                    </span>
                  </button>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                  {Object.entries(PLANES_INFO).map(([p, info]) => (
                    <button
                      key={p}
                      onClick={() => setForm({ ...form, plan: p })}
                      style={{
                        padding: '10px 8px', borderRadius: 10,
                        fontSize: 12, fontWeight: 700, textAlign: 'left',
                        border: form.plan === p ? `1px solid ${info.color}` : '1px solid var(--border)',
                        background: form.plan === p ? `${info.color}15` : 'var(--bg-secondary)',
                        color: form.plan === p ? info.color : 'var(--text-muted)',
                        cursor: 'pointer', fontFamily: "'DM Sans'",
                        transition: 'all 0.2s',
                      }}
                    >
                      <div>{info.label}</div>
                      <div style={{ fontSize: 10, fontWeight: 400, marginTop: 2, opacity: 0.8 }}>
                        {anual ? `$${info.anual}/año · ${info.desc_anual}` : `$${info.mensual}/mes · ${info.desc_mensual}`}
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              <input name="email" type="email" placeholder="Email" value={form.email}
                onChange={handleChange} className="input-dark" autoComplete="email" />
              <PasswordInput name="password" placeholder="Contraseña (mín. 8 caracteres)"
                value={form.password} onChange={handleChange} autoComplete="new-password" />
              <div style={{ position: 'relative' }}>
                <PasswordInput placeholder="Confirmá tu contraseña"
                  value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} autoComplete="new-password" />
                {confirmPassword && (
                  <span style={{
                    position: 'absolute', right: 42, top: '50%', transform: 'translateY(-50%)',
                    fontSize: 11, fontWeight: 700,
                    color: form.password === confirmPassword ? '#4ade80' : '#E63946',
                  }}>
                    {form.password === confirmPassword ? '✓ Coinciden' : '✗ No coinciden'}
                  </span>
                )}
              </div>
              {form.password && (
                <div style={{ marginTop: 8 }}>
                  <div style={{ height: 4, borderRadius: 2, background: 'rgba(255,255,255,0.06)', overflow: 'hidden', marginBottom: 6 }}>
                    <div style={{ height: '100%', width: `${fortaleza.pct}%`, background: fortaleza.color, borderRadius: 2, transition: 'width 0.3s, background 0.3s' }} />
                  </div>
                  {fortaleza.label && <p style={{ fontSize: 11, color: fortaleza.color, margin: '0 0 6px 0', fontWeight: 600 }}>{fortaleza.label}</p>}
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
                    {CHECK_LABELS.map((lbl, i) => (
                      <span key={i} style={{
                        fontSize: 10, padding: '2px 7px', borderRadius: 100,
                        background: fortaleza.checks[i] ? 'rgba(74,222,128,0.1)' : 'rgba(255,255,255,0.04)',
                        color: fortaleza.checks[i] ? '#4ade80' : '#666',
                        border: `1px solid ${fortaleza.checks[i] ? 'rgba(74,222,128,0.2)' : 'transparent'}`,
                      }}>
                        {fortaleza.checks[i] ? '✓' : '·'} {lbl}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Checkbox T&C */}
              <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer', fontSize: 13, color: 'var(--text-muted)', lineHeight: 1.5 }}>
                <input
                  type="checkbox"
                  checked={tcAceptado}
                  onChange={e => setTcAceptado(e.target.checked)}
                  style={{ marginTop: 2, accentColor: '#C9A84C', flexShrink: 0 }}
                />
                <span>
                  Acepto los{' '}
                  <Link to="/terminos" target="_blank" style={{ color: '#C9A84C', textDecoration: 'none', fontWeight: 600 }}>
                    Términos y Condiciones
                  </Link>{' '}y la{' '}
                  <Link to="/privacidad" target="_blank" style={{ color: '#C9A84C', textDecoration: 'none', fontWeight: 600 }}>
                    Política de Privacidad
                  </Link>
                </span>
              </label>

              <button onClick={handleRegistro} className="btn-gold" style={{ width: '100%', marginTop: 4, opacity: tcAceptado ? 1 : 0.5 }}>
                Continuar
              </button>
              <p style={{ textAlign: 'center', fontSize: 13, color: 'var(--text-muted)', margin: 0 }}>
                Ya tenes cuenta?{' '}
                <button onClick={() => navigate('/login')} style={{
                  background: 'none', border: 'none', color: '#C9A84C',
                  fontWeight: 600, cursor: 'pointer', fontSize: 13,
                  fontFamily: "'DM Sans'", padding: 0,
                }}>
                  Inicia sesion
                </button>
              </p>
            </div>
          )}

          {/* Paso 1: Barberia */}
          {paso === 1 && !verificacionPendiente && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <h2 style={{ fontFamily: "'Bebas Neue'", fontSize: 24, letterSpacing: '0.06em', margin: 0 }}>
                Configura tu barberia
              </h2>
              <input name="nombre_barberia" placeholder="Nombre de la barberia *"
                value={form.nombre_barberia} onChange={handleChange} className="input-dark" />
              <input name="direccion" placeholder="Direccion (opcional)"
                value={form.direccion} onChange={handleChange} className="input-dark" />
              <div style={{ display: 'flex' }}>
                <span style={{
                  padding: '0 12px', height: '42px', lineHeight: '42px',
                  background: 'var(--bg-secondary)', border: '1px solid var(--border)',
                  borderRight: 'none', borderRadius: '8px 0 0 8px',
                  fontSize: 13, color: 'var(--text-muted)', flexShrink: 0,
                }}>+506</span>
                <input
                  name="telefono"
                  placeholder="8888 8888 (opcional)"
                  value={form.telefono}
                  onChange={e => setForm({ ...form, telefono: formatearInput(e.target.value) })}
                  className="input-dark"
                  style={{ borderRadius: '0 8px 8px 0' }}
                  inputMode="numeric"
                />
              </div>
              <div style={{
                background: anual ? 'rgba(201,168,76,0.06)' : 'rgba(74,222,128,0.06)',
                border: `1px solid ${anual ? 'rgba(201,168,76,0.2)' : 'rgba(74,222,128,0.2)'}`,
                borderRadius: 10, padding: '12px 14px',
                display: 'flex', alignItems: 'flex-start', gap: 10,
              }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={anual ? '#C9A84C' : '#4ade80'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, marginTop: 1 }}>
                  <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
                </svg>
                <p style={{ fontSize: 12, color: anual ? '#C9A84C' : '#4ade80', margin: 0, lineHeight: 1.7, opacity: 0.9 }}>
                  {anual
                    ? <>Serás redirigido a Stripe para completar el pago de <strong>${form.plan === 'premium' ? '472' : '232'}/año</strong>. El acceso se activa de inmediato.</>
                    : form.plan === 'pro' ? <>Te pediremos una tarjeta para activar los <strong>14 días gratis</strong>. No se realiza ningún cobro hasta que termine el trial. Cancelá antes sin costo.</> : <>Serás redirigido a Stripe para completar el pago de <strong>${form.plan === 'premium' ? '59' : '29'}/mes</strong>. El acceso se activa de inmediato.</>
                  }
                </p>
              </div>
              <button onClick={handleBarberia} disabled={cargando} className="btn-gold"
                style={{ width: '100%', opacity: cargando ? 0.7 : 1 }}>
                {cargando ? 'Creando tu barberia...' : 'Crear mi barberia'}
              </button>
              <button onClick={() => setPaso(0)} style={{
                background: 'none', border: 'none', color: 'var(--text-muted)',
                fontSize: 13, cursor: 'pointer', fontFamily: "'DM Sans'",
              }}>
                Atras
              </button>
            </div>
          )}

          {/* Verificación de email pendiente */}
          {verificacionPendiente && (
            <div style={{ textAlign: 'center' }}>
              <div style={{
                width: 56, height: 56, borderRadius: '50%',
                background: 'rgba(201,168,76,0.12)', border: '1px solid rgba(201,168,76,0.3)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                margin: '0 auto 20px',
              }}>
                <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="#C9A84C" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                  <polyline points="22,6 12,13 2,6"/>
                </svg>
              </div>
              <h2 style={{ fontFamily: "'Bebas Neue'", fontSize: 26, letterSpacing: '0.06em', color: '#F5F5F5', margin: '0 0 10px 0' }}>
                Revisá tu email
              </h2>
              <p style={{ color: 'var(--text-muted)', fontSize: 14, margin: '0 0 6px 0', lineHeight: 1.7 }}>
                Te mandamos un enlace de activación a
              </p>
              <p style={{ color: '#C9A84C', fontSize: 14, fontWeight: 700, margin: '0 0 12px 0' }}>
                {form.email}
              </p>
              <p style={{ color: 'var(--text-muted)', fontSize: 13, margin: '0 0 24px 0', lineHeight: 1.7 }}>
                Abrí el email y hacé clic en el enlace para activar tu cuenta. Después podés iniciar sesión normalmente.
              </p>
              <button
                onClick={async () => {
                  try {
                    await reenviarVerificacion({ email: form.email });
                    alert('Email reenviado. Revisá también la carpeta de spam.');
                  } catch {
                    alert('No se pudo reenviar. Intentá de nuevo en unos minutos.');
                  }
                }}
                className="btn-gold"
                style={{ width: '100%', marginBottom: 12 }}
              >
                Reenviar email de activación
              </button>
              <button
                onClick={() => { setVerificacionPendiente(false); setPaso(0); }}
                style={{ background: 'none', border: 'none', color: 'var(--text-muted)', fontSize: 13, cursor: 'pointer', fontFamily: "'DM Sans'" }}
              >
                Usar otro email
              </button>
            </div>
          )}

          {/* Paso 2: Resumen + confirmación */}
          {paso === 2 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
              <div>
                <h2 style={{ fontFamily: "'Bebas Neue'", fontSize: 24, letterSpacing: '0.06em', margin: '0 0 4px' }}>
                  Confirmá tu plan
                </h2>
                <p style={{ fontSize: 13, color: 'var(--text-muted)', margin: 0 }}>
                  Revisá los detalles antes de continuar al pago
                </p>
              </div>

              {/* Card del plan */}
              <div style={{
                border: `1px solid ${PLANES_INFO[form.plan].color}40`,
                background: `${PLANES_INFO[form.plan].color}08`,
                borderRadius: 12, padding: '16px 18px',
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <div>
                    <p style={{ fontSize: 11, color: 'var(--text-muted)', margin: '0 0 4px', textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 700 }}>Plan</p>
                    <p style={{ fontSize: 18, fontWeight: 700, color: PLANES_INFO[form.plan].color, margin: '0 0 4px', fontFamily: "'Bebas Neue'", letterSpacing: '0.06em' }}>
                      {PLANES_INFO[form.plan].label}
                    </p>
                    <p style={{ fontSize: 12, color: 'var(--text-muted)', margin: 0 }}>
                      {anual ? PLANES_INFO[form.plan].desc_anual : PLANES_INFO[form.plan].desc_mensual}
                    </p>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <p style={{ fontSize: 28, fontWeight: 800, color: '#F5F5F5', margin: '0 0 2px', lineHeight: 1 }}>
                      ${anual ? PLANES_INFO[form.plan].anual : PLANES_INFO[form.plan].mensual}
                    </p>
                    <p style={{ fontSize: 12, color: 'var(--text-muted)', margin: 0 }}>/{anual ? 'año' : 'mes'}</p>
                  </div>
                </div>
                {!anual && form.plan === 'pro' && (
                  <div style={{
                    marginTop: 12, paddingTop: 12, borderTop: `1px solid ${PLANES_INFO[form.plan].color}25`,
                    fontSize: 12, color: '#4ade80', fontWeight: 600,
                  }}>
                    14 dias gratis incluidos · sin cobro hasta que termine el trial
                  </div>
                )}
              </div>

              {/* Datos de la barberia */}
              <div style={{
                background: 'rgba(255,255,255,0.03)', border: '1px solid var(--border)',
                borderRadius: 10, padding: '12px 16px',
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              }}>
                <span style={{ fontSize: 12, color: 'var(--text-muted)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Barberia</span>
                <span style={{ fontSize: 14, color: '#F5F5F5', fontWeight: 600 }}>{form.nombre_barberia}</span>
              </div>

              {stripeError && (
                <div style={{
                  background: 'rgba(230,57,70,0.08)', border: '1px solid rgba(230,57,70,0.25)',
                  borderRadius: 10, padding: '10px 14px', fontSize: 13, color: '#E63946',
                }}>
                  No se pudo conectar con el sistema de pago. Intentá de nuevo.
                </div>
              )}

              <button onClick={handleIrAStripe} disabled={cargando} className="btn-gold"
                style={{ width: '100%', opacity: cargando ? 0.7 : 1, fontSize: 15 }}>
                {cargando ? 'Conectando con Stripe...' : 'Confirmar y pagar →'}
              </button>

              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#555" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                  <path d="M7 11V7a5 5 0 0110 0v4"/>
                </svg>
                <p style={{ fontSize: 11, color: '#555', margin: 0 }}>
                  Pago procesado de forma segura por Stripe. No almacenamos datos de tarjeta.
                </p>
              </div>

              <button onClick={() => { setPaso(1); setStripeError(false); setTempToken(''); }}
                style={{ background: 'none', border: 'none', color: 'var(--text-muted)', fontSize: 13, cursor: 'pointer', fontFamily: "'DM Sans'" }}>
                Atras
              </button>
            </div>
          )}
          </div>{/* /padding */}
        </div>
      </div>
    </div>
  );
}
